import { useLocation, useResolvedPath } from "react-router-dom";

/**
 * Whether `href` corresponds to the page the user is currently on.
 *
 * Replaces the active-item highlighting that `SidebarItem` from
 * `@backstage/core-components` did internally.
 *
 * Two cases need care:
 *
 * - **Nested routes.** `/catalog` must stay highlighted while the user is on
 *   `/catalog/default/component/foo`, so a path prefix counts as a match. The
 *   root path `/` is excluded from prefix matching, since it prefixes
 *   everything and would otherwise leave Home permanently active.
 * - **Query-scoped links.** The catalog submenu points several items at the
 *   same `/catalog` path, distinguished only by `?filters[kind]=...`. When the
 *   href carries a search string it must match exactly, otherwise every kind
 *   would highlight at once.
 *
 * @param href - Target of the nav item, relative or absolute.
 * @returns `true` when the current location matches.
 */
export function useIsActiveRoute(href: string): boolean {
    const location = useLocation();
    const resolved = useResolvedPath(href);

    if (resolved.search) {
        return (
            location.pathname === resolved.pathname &&
            location.search === resolved.search
        );
    }

    if (resolved.pathname === "/") {
        return location.pathname === "/";
    }

    return (
        location.pathname === resolved.pathname ||
        location.pathname.startsWith(`${resolved.pathname}/`)
    );
}
